import { normalizeText } from './TextHelpers';
import { ExpandedBackfillRecord } from './CertBackfillHelpers';
import { WorkdayCertification } from '../types/Workday/WorkdayCertification';
import { CERTIFIER_EMAIL_BY_LOCAL_PART } from '../constants/CertifierEmailOverrides';
import { GENERAL_SUPERINTENDENT } from '../constants/JobCodes';

/**
 * A reason a record cannot yet be certified as-is. Each maps to one missing/ambiguous
 * input that {@link resolveRecord} could not fill in.
 */
export type ResolutionIssue =
    | 'invalid-emp-no'        // Emp No. is missing or not a usable number
    | 'employee-not-found'    // no active Blattner employee has this Emp No., so the row is unverifiable
    | 'employee-id-mismatch'  // the Emp No. resolves to someone the Certified Trainer email does not belong to
    | 'invalid-dates'         // issued date is missing, or not before the expiration date
    | 'unmapped-cert'         // the mapped cert name has no matching WorkdayCertification
    | 'certifier-blank'       // the source row has no Certified By value
    | 'certifier-not-found'   // no Certified By email resolved to a directory user
    | 'certifier-not-gs';     // the certifier resolved but isn't a GS (allowed only with the bypass)

/** Human-readable label for each resolution issue, shown in the review table. */
export const RESOLUTION_ISSUE_LABEL: Readonly<Record<ResolutionIssue, string>> = {
    'invalid-emp-no': 'Emp No. is not a valid number',
    'invalid-dates': 'Effective Date is missing or is not before the Expiration Date',
    'employee-not-found': 'Emp No. does not match an active Blattner employee',
    'employee-id-mismatch': 'Emp No. and Certified Trainer email are different people',
    'unmapped-cert': 'No matching certification in Workday',
    'certifier-blank': 'No "Certified By" in source',
    'certifier-not-found': 'Certified By email not found in the directory',
    'certifier-not-gs': 'Certified By is not a General Superintendent (enable bypass to allow)',
};

/** A Certified By email resolved (or not) to a directory user via getUserByEmail. */
export interface ResolvedCertifier {
    email: string;                    // the original email from the CSV
    employeeId: number | null;        // null when the lookup found no user
    jobCode: string | null;
    name: string | null;
    isGeneralSuperintendent: boolean;
}

/** An expanded record with the ids certifyEmployee needs resolved, plus any issues blocking it. */
export interface ResolvedBackfillRecord {
    source: ExpandedBackfillRecord;
    employeeId: number | null;
    certificationId: number | null;
    certifiedById: number | null;
    certifierName: string | null;   // display name of the chosen certifier, when accepted
    issues: ResolutionIssue[];
    ready: boolean;                 // true when there are no issues — safe to certify
}

/** Reference data resolved once (from network) and reused across every record. */
export interface BackfillReferenceData {
    certIdByName: Map<string, number>;                // normalized cert name -> WorkdayCertification id
    certifierByEmail: Map<string, ResolvedCertifier>; // normalized original email -> resolution
    employeeById: Map<number, ResolvedEmployee>;      // Emp No. -> the directory employee it identifies
}

/** Options that affect resolution without needing a fresh network load. */
export interface ResolveOptions {
    /** When true, a resolved certifier is accepted even if they are not a General Superintendent. */
    allowNonGsCertifier: boolean;
}

/**
 * Rewrites a Blattner email to the Quanta directory identity used by getUserByEmail: the local part
 * is kept and the domain is forced to quantaservices.com. Blattner addresses (blattnercompany.com and
 * blattnerenergy.com) all resolve under the Quanta tenant, so every domain is normalized the same way.
 */
export const toQuantaEmail = (email: string): string => {
    const local = (email ?? '').split('@')[0].trim();
    return local ? `${local}@quantaservices.com` : '';
};

/**
 * The explicitly mapped directory address for a Certified By email, for the people whose directory
 * identity the domain rewrite cannot produce (see CERTIFIER_EMAIL_BY_LOCAL_PART).
 *
 * @param email - The Certified By address from the CSV.
 * @returns The mapped address, or '' when no override applies.
 */
export const overrideCertifierEmail = (email: string): string => {
    const local = normalizeText((email ?? '').split('@')[0]);
    return local ? (CERTIFIER_EMAIL_BY_LOCAL_PART[local] ?? '') : '';
};

/**
 * The directory addresses to try for one Certified By email, best candidate first:
 * an explicit override, then the Quanta-normalized form, then the address exactly as written.
 * Duplicates and blanks are removed, so a CSV address already in Quanta form yields one attempt.
 *
 * @param email - The Certified By address from the CSV.
 * @returns Ordered, de-duplicated addresses to look up.
 */
export const certifierEmailCandidates = (email: string): string[] => {
    const candidates = [overrideCertifierEmail(email), toQuantaEmail(email), (email ?? '').trim()];
    const seen = new Set<string>();
    return candidates.filter(c => {
        const key = normalizeText(c);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

/**
 * Splits a Certified By cell into individual certifier emails.
 *
 * Accepts both delimiters: the current export separates people with a plain ';', while SharePoint's
 * multi-value form is ';#'. Handling both matters because resolution prefers a General Superintendent when
 * several certifiers are listed — if the second one were dropped at parse time, a row naming a valid GS
 * second would be blocked as certifier-not-gs, silently and for no visible reason.
 *
 * @param raw - The Certified By cell, verbatim.
 * @returns Each non-empty certifier email, trimmed.
 */
export const splitCertifierEmails = (raw: string): string[] =>
    (raw ?? '').split(/;#?/).map(e => e.trim()).filter(Boolean);

/**
 * A directory employee looked up by employee number, carrying both addresses they are known by so a
 * Certified Trainer email from the export can be checked against either.
 */
export interface ResolvedEmployee {
    employeeId: number;
    name: string | null;
    signInEmail: string | null;    // userPrincipalName — the @quantaservices.com form
    outsideEmail: string | null;   // Graph 'mail' — the Blattner-domain form the export uses
}

/** Every distinct Emp No. across the given records, for a one-time directory lookup. */
export const distinctEmployeeIds = (records: ExpandedBackfillRecord[]): string[] => {
    const seen = new Set<string>();
    for (const rec of records) {
        const empNo = (rec.empNo ?? '').trim();
        if (empNo) seen.add(empNo);
    }
    return Array.from(seen);
};

/**
 * True when an address from the export belongs to the given employee — matching either address the
 * directory holds for them, since a person's sign-in address and outward-facing address routinely differ
 * by more than domain (CParks@blattnerenergy.com signs in as Camden.Parks@quantaservices.com).
 *
 * @param email - The Certified Trainer address from the export.
 * @param employee - The employee the row's Emp No. resolved to.
 * @returns True when the address is one of that employee's.
 */
export const addressBelongsToEmployee = (email: string, employee: ResolvedEmployee): boolean => {
    const target = normalizeText(email ?? '');
    if (!target) return false;
    return target === normalizeText(employee.signInEmail ?? '')
        || target === normalizeText(employee.outsideEmail ?? '');
};

/** Every distinct certifier email across the given records (for a one-time directory lookup). */
export const distinctCertifierEmails = (records: ExpandedBackfillRecord[]): string[] => {
    const seen = new Set<string>();
    for (const rec of records) {
        for (const email of splitCertifierEmails(rec.certifiedByName)) seen.add(email);
    }
    return Array.from(seen);
};

/** Indexes the WorkdayCertification list by normalized name for certName -> id resolution. */
export const buildCertIndex = (certs: WorkdayCertification[]): Map<string, number> =>
    new Map(certs.map(c => [normalizeText(c.name), c.id]));

/** True when a directory job code is the General Superintendent code. */
export const isGeneralSuperintendent = (jobCode: string | null | undefined): boolean =>
    jobCode === GENERAL_SUPERINTENDENT;

/**
 * True when a record's dates would be rejected by certifyEmployee, which requires an issued date it can
 * bind and an expiration strictly after it.
 *
 * Only unexpired rows reach resolution — `kept` derives from `rows.filter(isUnexpired)` — so this never
 * fires on a row that is simply out of date. It catches the narrower case of a row that is still live but
 * internally inconsistent, which would otherwise pass as Ready and 400 only once it was POSTed.
 *
 * Dates are already normalized to YYYY-MM-DD, so comparing them as strings is ordering-safe.
 *
 * @param rec - The expanded record to check.
 * @returns True when the dates are unusable.
 */
export const hasInvalidDates = (rec: ExpandedBackfillRecord): boolean => {
    const issued = (rec.issuedDate ?? '').trim();
    const expiration = (rec.expirationDate ?? '').trim();
    if (!issued || !expiration) return true;
    return expiration <= issued;   // the backend requires expiration strictly after issued
};

/** Resolves one expanded record into the inputs certifyEmployee needs, given the reference data + options. */
export const resolveRecord = (
    rec: ExpandedBackfillRecord,
    ref: BackfillReferenceData,
    opts: ResolveOptions,
): ResolvedBackfillRecord => {
    const issues: ResolutionIssue[] = [];

    const employeeId = Number(rec.empNo);
    const employeeIdValid = !!rec.empNo && Number.isFinite(employeeId);
    if (!employeeIdValid) {
        issues.push('invalid-emp-no');
    } else {
        // The export's Emp No. and Certified Trainer email can disagree — at least one row pairs one
        // person's email with another person's id. Emp No. is used directly as the certified employee, so
        // an unverified row would certify the wrong person. Flag it rather than pick a winner.
        //
        // Checked Emp No. -> employee, not email -> employee: the Emp No. is always present and is an exact
        // key, and resolving it yields BOTH of that person's addresses, so the row's email can be matched
        // against either. Going the other way depends on the email resolving at all, which is the part that
        // fails for accounts whose two addresses differ by more than domain.
        const employee = ref.employeeById.get(employeeId);
        if (!employee) issues.push('employee-not-found');
        else if (!addressBelongsToEmployee(rec.trainerName, employee)) issues.push('employee-id-mismatch');
    }

    if (hasInvalidDates(rec)) issues.push('invalid-dates');

    const certificationId = ref.certIdByName.get(normalizeText(rec.certName)) ?? null;
    if (certificationId === null) issues.push('unmapped-cert');

    let certifiedById: number | null = null;
    let certifierName: string | null = null;

    const emails = splitCertifierEmails(rec.certifiedByName);
    if (emails.length === 0) {
        issues.push('certifier-blank');
    } else {
        const resolved = emails
            .map(e => ref.certifierByEmail.get(normalizeText(e)))
            .filter((r): r is ResolvedCertifier => !!r && r.employeeId !== null);

        if (resolved.length === 0) {
            issues.push('certifier-not-found');
        } else {
            // When two certifiers are listed, keep the valid GS (per AC); otherwise take the first resolved.
            const chosen = resolved.find(r => r.isGeneralSuperintendent) ?? resolved[0];
            if (!chosen.isGeneralSuperintendent && !opts.allowNonGsCertifier) {
                issues.push('certifier-not-gs');
            } else {
                certifiedById = chosen.employeeId;
                certifierName = chosen.name;
            }
        }
    }

    return {
        source: rec,
        employeeId: employeeIdValid ? employeeId : null,
        certificationId,
        certifiedById,
        certifierName,
        issues,
        ready: issues.length === 0,
    };
};

/** The issues that supplying a Certified By fixes — every one is about that single value. */
const CERTIFIER_ISSUES: ReadonlySet<ResolutionIssue> = new Set<ResolutionIssue>([
    'certifier-blank',
    'certifier-not-found',
    'certifier-not-gs',
]);

/**
 * True when Certified By is the only thing blocking a record, so choosing one makes it importable.
 *
 * Deliberately requires *every* issue to be certifier-related: a row that is also missing its
 * employee or certification stays unfixable, and offering a picker there would only produce a
 * certifyEmployee call that is guaranteed to fail.
 *
 * @param rec - A resolved record.
 * @returns True when assigning a certifier would make the record ready.
 */
export const canAssignCertifier = (rec: ResolvedBackfillRecord): boolean =>
    !rec.ready && rec.issues.length > 0 && rec.issues.every(i => CERTIFIER_ISSUES.has(i));

/**
 * Replaces a record's Certified By with a manually chosen employee and clears the certifier issues.
 *
 * For rows the SharePoint export left blank, or whose certifier could not be resolved, where a
 * reviewer supplies the General Superintendent instead.
 *
 * @param rec - The record to fix.
 * @param certifier - The chosen employee's id and display name.
 * @returns A copy with the certifier applied; ready when nothing else was blocking it.
 */
export const applyAssignedCertifier = (
    rec: ResolvedBackfillRecord,
    certifier: { employeeId: number; name: string },
): ResolvedBackfillRecord => {
    const issues = rec.issues.filter(i => !CERTIFIER_ISSUES.has(i));
    return {
        ...rec,
        certifiedById: certifier.employeeId,
        certifierName: certifier.name,
        issues,
        ready: issues.length === 0,
    };
};

/** Resolves every expanded record against the reference data. */
export const resolveRecords = (
    records: ExpandedBackfillRecord[],
    ref: BackfillReferenceData,
    opts: ResolveOptions,
): ResolvedBackfillRecord[] => records.map(rec => resolveRecord(rec, ref, opts));
